import React, { useState, useEffect } from 'react';
import { Send, X, FileText, PenLine, Loader2, ExternalLink } from 'lucide-react';
import { interviewInvitationsApi } from '../../services/interviewInvitationsApi';
import { invitationLetterService, InvitationLetter } from '../../services/invitationLetterService';
import './InviteToInterviewModal.scss';

interface InviteToInterviewModalProps {
  candidateUserId: number;
  candidateName: string;
  onClose: () => void;
  onSent?: (invitationId: number) => void;
}

const MAX_INTRO_LENGTH = 4000;

type MessageMode = 'letter' | 'text';

/**
 * "Pakviesti pokalbiui" modal — the employer can either pick a saved
 * invitation letter OR type a free-form intro message, then send the invite.
 */
const InviteToInterviewModal: React.FC<InviteToInterviewModalProps> = ({
  candidateUserId,
  candidateName,
  onClose,
  onSent,
}) => {
  const [mode, setMode] = useState<MessageMode>('letter');
  const [letters, setLetters] = useState<InvitationLetter[]>([]);
  const [lettersLoading, setLettersLoading] = useState(true);
  const [selectedLetterId, setSelectedLetterId] = useState<number | null>(null);
  const [introMessage, setIntroMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remaining = MAX_INTRO_LENGTH - introMessage.length;

  useEffect(() => {
    invitationLetterService.getAll()
      .then((data) => {
        setLetters(data || []);
        // If no letters, default to free-text mode
        if (!data || data.length === 0) setMode('text');
      })
      .catch(() => setMode('text'))
      .finally(() => setLettersLoading(false));
  }, []);

  const getMessageToSend = (): string | undefined => {
    if (mode === 'letter') {
      const letter = letters.find((l) => l.id === selectedLetterId);
      const text = letter?.content?.trim() ?? '';
      return text.length > 0 ? text : undefined;
    }
    const trimmed = introMessage.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const { invitation } = await interviewInvitationsApi.create(
        candidateUserId,
        getMessageToSend(),
      );
      onSent?.(invitation.id);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Nepavyko išsiųsti kvietimo');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="invite-interview-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-interview-title"
      >
        <button
          className="btn-close-modal"
          onClick={onClose}
          aria-label="Uždaryti"
          type="button"
        >
          <X size={22} />
        </button>

        <div className="invite-interview-modal__header">
          <h2 id="invite-interview-title">Pakviesti pokalbiui</h2>
          <p className="candidate-name">{candidateName}</p>
        </div>

        <p className="modal-helper">
          Kandidatas gaus el. laišką su jūsų kvietimu. Patvirtinus, galėsite
          rašyti vienas kitam susirašinėjimo lange.
        </p>

        {error && <div className="modal-error">{error}</div>}

        {/* Mode toggle */}
        <div className="message-mode-toggle">
          <button
            type="button"
            className={`mode-btn ${mode === 'letter' ? 'active' : ''}`}
            onClick={() => setMode('letter')}
          >
            <FileText size={15} />
            Pasirinkti laišką
          </button>
          <button
            type="button"
            className={`mode-btn ${mode === 'text' ? 'active' : ''}`}
            onClick={() => setMode('text')}
          >
            <PenLine size={15} />
            Rašyti tekstą
          </button>
        </div>

        {/* Letter picker */}
        {mode === 'letter' && (
          <div className="letter-picker">
            {lettersLoading ? (
              <div className="letter-picker__loading">
                <Loader2 size={18} className="spin" />
                Kraunami laiškai...
              </div>
            ) : letters.length === 0 ? (
              <div className="letter-picker__empty">
                <p>Neturite išsaugotų kvietimo laiškų.</p>
                <a
                  href="/employer/invitation-letters?mode=employer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="letter-picker__create-link"
                >
                  <ExternalLink size={13} />
                  Sukurti laišką
                </a>
              </div>
            ) : (
              <ul className="letter-list">
                {letters.map((letter) => (
                  <li
                    key={letter.id}
                    className={`letter-item ${selectedLetterId === letter.id ? 'selected' : ''}`}
                    onClick={() => setSelectedLetterId(
                      selectedLetterId === letter.id ? null : letter.id!
                    )}
                  >
                    <div className="letter-item__radio">
                      <div className={`radio-dot ${selectedLetterId === letter.id ? 'active' : ''}`} />
                    </div>
                    <div className="letter-item__content">
                      <span className="letter-item__title">{letter.title}</span>
                      <span className="letter-item__preview">
                        {letter.content.slice(0, 100)}{letter.content.length > 100 ? '…' : ''}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Free text */}
        {mode === 'text' && (
          <>
            <label className="form-label" htmlFor="invite-intro">
              Žinutė kandidatui (neprivaloma)
            </label>
            <textarea
              id="invite-intro"
              className="invite-intro"
              value={introMessage}
              onChange={(e) => setIntroMessage(e.target.value.slice(0, MAX_INTRO_LENGTH))}
              placeholder="Trumpai pristatykite, kodėl jį/ją kviečiate (pvz. apie poziciją, įmonę, darbo pobūdį)"
              rows={6}
              maxLength={MAX_INTRO_LENGTH}
            />
            <div className="char-count">
              {remaining < 0 ? 0 : remaining} simbolių liko
            </div>
          </>
        )}

        <div className="invite-interview-modal__actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Atšaukti
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            <Send size={16} />
            {submitting ? 'Siunčiama...' : 'Išsiųsti kvietimą'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteToInterviewModal;
